import { useMemo, useState, useEffect, type FC, useRef } from 'react';
import type { HeroDetailedProps } from './types';
import { shade } from 'shared/helpers/utils';

import styled, { css } from 'styled-components';
import {
  COLOR_YELLOW,
  COLOR_LIGHTER_GRAY,
  defaultWebValue,
  filterBackgroundColor,
  sanitizeRichText,
  generateAccessKey
} from 'shared/helpers/index';
import { useBook, useSales, useWebsiteEditor, useSublineColor } from 'shared/hooks/index';

import { Button, Wrapper, Mockup, Download } from '../Common';
import useIsEmpty from 'shared/hooks/useIsEmpty';
import { Image as ImageComp } from 'shared/components/Common';
import { ConditionalImage } from '../ConditionalImage';

import SvgIcons from 'shared/components/Common/IconsHeroDetailed';
import ArrowsSvg from 'shared/components/Common/ArrowsSvg';
import useGeneralActions from '../hooks/useGeneralActions';

const HeroDetailed = styled.div`
  position: relative;
  overflow-x: hidden;
  min-height: 693px;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
  flex-direction: column;

  `}
`;

const StyledWrapper = styled((props) => <Wrapper {...props} />)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 98px 0 0px 0px;
  max-width: 100%;
  gap: 65px;
  position: relative;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
   padding: 78px 0 0px 0px;
   gap: 48px;
    `}
`;

const TitleWrapper = styled.div`
  position: relative;
  z-index: 7;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 80%;
  margin: 0 auto;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 100%;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    padding: 0 15px;
    `}
`;

const Title = styled.h1<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $largeHeadlineFont } }) => $largeHeadlineFont};
  line-height: 1.25em;
  color: #fff;
  text-align: center;
  margin: 0;
  margin-bottom: 20px;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 100%;
    text-align: center;
    `}
`;

const Description = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  line-height: 1.35em;
  color: #fff;
  padding-top: 8px;
  text-align: center;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
      width: 100%;
      text-align: center;
      margin-bottom: 0px;
      `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const ButtonInner = styled.div`
  position: relative;
  width: fit-content;
  z-index: 12;
  margin: 0 auto;

  @media screen and (max-width: 575px) {
    width: 100%;
    z-index: 5;
    margin: 0 auto;
    display: flex;
    justify-content: center;
  }
`;

const BtnInner = styled.div`
  display: flex;
  justify-content: center;
`;

const buttonStyles = css`
  max-width: 425px;
  display: inline-block;
  padding-left: 50px;
  padding-right: 50px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
      max-width: 345px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
      max-width: 380px;
      padding: 19px 32px;
    `}
`;

const DownloadStyled = styled(Download)`
  ${buttonStyles}
`;

const StyledButton = styled(Button)`
  ${buttonStyles}
`;

const ArrowsSvgStyled = styled(ArrowsSvg)`
  position: absolute;
  right: -116px;
  width: 90px;
  top: -40px;
  opacity: 0.42;
  z-index: -1;

  @media screen and (max-width: 900px) {
    display: none;
  }
`;

const StyledMockup = styled((props) => <Mockup {...props} />)`
  width: 506px;
  height: auto;
  object-fit: fill;
  z-index: 2;
  position: relative;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 438px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    width: 340px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 370 &&
    `
    width: 270px;
  `}
`;

const SvgStar = styled.svg`
  width: 93px;
  height: 93px;
  position: absolute;
  z-index: 5;
  right: -100px;
  top: 55%;
  opacity: 0.95;

  @media screen and (max-width: 991px) {
    width: 70px;
    height: 70px;
    opacity: 0.5;
  }

  @media screen and (max-width: 575px) {
    display: none;
  }
`;

const SvgStar2 = styled.svg`
  width: 93px;
  height: 93px;
  position: absolute;
  z-index: 5;
  left: -100px;
  top: 82%;
  opacity: 0.95;

  @media screen and (max-width: 991px) {
    width: 70px;
    height: 70px;
    opacity: 0.5;
  }

  @media screen and (max-width: 575px) {
    display: none;
  }
`;

const BenefitWrapper = styled.div`
  position: relative;
  margin-top: 28px;
`;

const Benefits = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  position: relative;
  z-index: 24;
  padding: 50px 0;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 100%;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    flex-direction: column;
    bottom: auto;
    padding-bottom: 35px;
    padding-top: 50px;
    `}
`;

const BenefitItem = styled.div`
  width: 30%;
  display: flex;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    flex-direction: row;
    width: 100%;
    padding: 18px 15px;
    border-radius: 12px;

    &:not(:last-child) {
      margin-bottom: 10px;
    }
    `}
`;

const StyledSvgIcons = styled(SvgIcons)`
  width: 48px;
  height: 48px;
  margin-right: 18px;
  border-radius: 50%;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 62px;
    height: 62px;
    margin-bottom: 8px;
  `}
`;

const BenefitTxtInner = styled.div`
  width: 83%;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
      width: 100%;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
      width: 80%;
    `}
`;

const TitleBenefit = styled.h2<{ isEmpty?: boolean; editable?: boolean }>`
  margin-bottom: 6px;
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  font-weight: 500;
  line-height: 1.35em;

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    margin-bottom: 4px;
    `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const TextBenefit = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $tinyTextFont } }) => $tinyTextFont};
  line-height: 1.3em;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const WhiteInner = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  z-index: 3;
  overflow: hidden;
`;

const RelativeWhite = styled.div`
  position: relative;
  width: 1920px;
  height: 100%;
  margin: 0 auto;

  .hero-white-v5 {
    border-radius: 50%;
    filter: blur(88px);
    background: #fff;
    position: absolute;
    margin: 0 auto;
    left: 0;
    right: 0;
  }

  .hero-white-v5_one {
    width: 810px;
    height: 810px;
    opacity: 0.65;
    top: -10%;
    left: -64%;
  }

  .hero-white-v5_two {
    width: 872px;
    height: 872px;
    opacity: 0.65;
    background: #fff;
    left: 0;
    right: 0;
    margin: 0 auto;
    bottom: 25%;
  }

  .hero-white-v5_three {
    width: 810px;
    height: 810px;
    opacity: 0.65;
    top: -10%;
    right: -64%;
  }

  @media screen and (max-width: 575px) {
    .hero-white-v5_one {
      width: 872px;
      height: 872px;
      opacity: 0.4;
      background: #fff;
      left: 0;
      right: 0;
      margin: 0 auto;
      bottom: 25%;
    }
  }
`;

const ColoredBg = styled.div`
  position: absolute;
  width: 160%;
  height: 72%;
  top: 0;
  border-top: 1px solid rgb(255 255 255 / 16%);
  border-radius: 0 0 50% 50%;
  left: -30%;

  @media screen and (max-width: 575px) {
    width: 200%;
    left: -50%;
  }
`;

const ImageGallery = styled.div<{ count: number }>`
  display: flex;
  justify-content: center;
  align-items: center;
  perspective: 1540px;
  transform-style: preserve-3d;
  padding-top: ${({ theme: { $width } }) => ($width <= 991 ? '90px' : '120px')};
  z-index: 22;
  margin: 0 auto;
  left: 0.1%;
  gap: 10px;
  max-width: 2400px;
  width: 81%;
  position: relative;
  justify-content: space-between;

  .det-hero-v6 {
    width: 21.5vw;
    aspect-ratio: 1 / 1.4142;
    margin: 0 10px;
    position: relative;
    filter: drop-shadow(10px 10px 20px rgba(0, 0, 0, 0.22));
    display: flex;
    justify-content: center;
    align-items: center;
    transform-style: preserve-3d;
    will-change: transform;

    @media screen and (max-width: 575px) {
      filter: drop-shadow(2px 10px 10px rgba(0, 0, 0, 0.22));
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  ${({ count }) =>
    count < 2
      ? `
        display: flex;
        justify-content: center;

         .det-hero-v6 {
            width: fit-content;
            aspect-ratio: auto;
            margin: 0;
         }
      `
      : count === 2
        ? `
      max-width: 1360px;
      width: 100%;
      justify-content: center;
      margin-bottom: 70px;

  .det-hero-v6 {
    width: 21.5vw;
    aspect-ratio: 1 / 1.4142;
    position: relative;
    display: flex;
    justify-content: center;
  }

  .det-hero-v6:nth-child(1) {
    transform: rotateY(30deg) scale(1.04) translateX(-20px) translate3d(-50px, 7px, 20px);
    z-index: 2;
  }

  .det-hero-v6:nth-child(2) {
    transform: rotateY(0deg) scale(1.1);
    z-index: 3;
  }

  .det-hero-v6:nth-child(3) {
    transform: rotateY(-30deg) scale(1.04) translateX(20px) translate3d(50px, 7px, 20px);
    z-index: 2;
  }

  @media screen and (max-width: 1560px) {
    perspective: 1340px;
    width: 86%;
    gap: 15px;
    .det-hero-v6 {
      width: 21vw;
    }

    .det-hero-v6:nth-child(1) {
      transform: rotateY(30deg) scale(1.15) translateX(-40px) translate3d(-50px, 7px, 20px);
    }

    .det-hero-v6:nth-child(2) {
      transform: rotateY(0deg) scale(1);
    }

    .det-hero-v6:nth-child(3) {
      transform: rotateY(-30deg) scale(1.15) translateX(40px) translate3d(50px, 7px, 20px);
    }
  }

  @media screen and (max-width: 1380px) and (min-width: 1000px) {
    gap: 10px;
    
    .det-hero-v6 {
      width: 29vw;
    }

    .det-hero-v6:nth-child(1) {
      transform: rotateY(30deg) scale(1) translateX(-20px) translate3d(-50px, 7px, 20px);
    }

    .det-hero-v6:nth-child(3) {
      transform: rotateY(-30deg) scale(1) translateX(20px) translate3d(50px, 7px, 20px);
    }
  }

  @media screen and (max-width: 991px) {
    width: 100%;
    gap: 0;
    perspective: 1000px;

    .det-hero-v6:nth-child(1) {
      transform: rotateY(30deg) scale(1.5) translateX(-70px) translate3d(-50px, 7px, 20px);
    }

    .det-hero-v6:nth-child(2) {
      transform: scale(1.0);
    }

    .det-hero-v6:nth-child(3) {
      transform: rotateY(-30deg) scale(1.5) translateX(70px) translate3d(50px, 7px, 20px);
    }

  }

  @media screen and (max-width: 465px) {
    margin-bottom: 40px;

    .det-hero-v6 {
      width: 220px;
    }

    .det-hero-v6:nth-child(1) {
      transform: rotateY(30deg) scale(3.5) translateX(33px) translate3d(-60px, 0px, 20px);
    }

    .det-hero-v6:nth-child(2) {
      transform: rotateY(0deg) scale(1.0);
      width: 90vw;
    }

    .det-hero-v6:nth-child(3) {
      transform: rotateY(-30deg) scale(3.5) translateX(-33px) translate3d(60px, 0px, 20px);
    }
  }
    `
        : `
      perspective: 1400px;

       .det-hero-v6:nth-child(1) {
          transform: rotateY(22deg) scale(1.02) translateX(-72px) translate3d(-53px, 9px, -160px);
          overflow: hidden;
          z-index: 3;
        }
        .det-hero-v6:nth-child(2) {
          @media screen and (min-width: 2300px) {
            transform: rotateY(15deg) translateX(-30px) scale(1.04);
          }

          @media screen and (min-width: 1921px and max-width: 2300px) {
            transform: rotateY(15deg) translateX(-44px) scale(1.05);
          }

          transform: rotateY(15deg) translateX(-40px) scale(1.02);
          overflow: hidden;
          z-index: 2;
        }

        .det-hero-v6:nth-child(3) {
          transform: rotateY(0deg) scale(1);
        }

        .det-hero-v6:nth-child(4) {
          @media screen and (min-width: 2300px) {
            transform: rotateY(-15deg) translateX(30px) scale(1.05);
          }

          @media screen and (min-width: 1921px and max-width: 2300px) {
            transform: rotateY(-15deg) translateX(40px) scale(1.04);
          }

          transform: rotateY(-15deg) translateX(40px) scale(1.02);
          overflow: hidden;
          z-index: 2;
        }
        .det-hero-v6:nth-child(5) {
          transform: rotateY(-22deg) scale(1.02) translateX(72px) translate3d(53px, 9px, -160px);
          overflow: hidden;
          z-index: 3;
        }

      @media screen and (max-width: 1920px) {
        .det-hero-v6:nth-child(1) {
          transform: rotateY(22deg) scale(1.02) translateX(-82px) translate3d(-53px, 9px, -160px);
        }

        .det-hero-v6:nth-child(2) {
          transform: rotateY(15deg) translateX(-62px) scale(1.07);
        }

        .det-hero-v6:nth-child(4) {
          transform: rotateY(-15deg) translateX(62px) scale(1.07);
        }

        .det-hero-v6:nth-child(5) {
          transform: rotateY(-22deg) scale(1.02) translateX(82px) translate3d(53px, 9px, -160px);
        }

         // Safari styles
        .det-hero-v6_safari:nth-child(2) {
          transform: rotateY(15deg) translateX(-42px) scale(1.07);
        }

        .det-hero-v6_safari:nth-child(4) {
          transform: rotateY(-15deg) translateX(42px) scale(1.07);
        }
      }

      @media screen and (max-width: 1770px) {
        .det-hero-v6:nth-child(2) {
          transform: rotateY(15deg) translateX(-76px) scale(1.07);
        }

        .det-hero-v6:nth-child(4) {
          transform: rotateY(-15deg) translateX(76px) scale(1.07);
        }

         // Safari styles
        .det-hero-v6_safari:nth-child(2) {
          transform: rotateY(15deg) translateX(-56px) scale(1.07);
        }

        .det-hero-v6_safari:nth-child(4) {
          transform: rotateY(-15deg) translateX(56px) scale(1.07);
        }
          
      }

      @media screen and (max-width: 1560px) {
        .det-hero-v6:nth-child(1) {
          transform: rotateY(22deg) scale(1.02) translateX(-90px) translate3d(-53px, 9px, -160px);
        }

        .det-hero-v6:nth-child(2) {
          transform: rotateY(15deg) translateX(-6.4vw) scale(1.2);
        }

        .det-hero-v6:nth-child(4) {
          transform: rotateY(-15deg) translateX(6.4vw) scale(1.2);
        }

        .det-hero-v6:nth-child(5) {
          transform: rotateY(-22deg) scale(1.02) translateX(90px) translate3d(53px, 9px, -160px);
        }
      
        // Safari styles
        .det-hero-v6_safari:nth-child(2) {
          transform: rotateY(15deg) translateX(-3.4vw) scale(1.2);
        }

        .det-hero-v6_safari:nth-child(4) {
          transform: rotateY(-15deg) translateX(3.4vw) scale(1.2);
        }
        
      } 

      @media screen and (max-width: 1320px) {
        .det-hero-v6:nth-child(1) {
          transform: rotateY(22deg) scale(1.02) translateX(-2.5vw) translate3d(-53px, 9px, -160px);
        }

        .det-hero-v6:nth-child(2) {
          transform: rotateY(15deg) translateX(-4.85vw) scale(1.07);
        }

        .det-hero-v6:nth-child(4) {
          transform: rotateY(-15deg) translateX(4.85vw) scale(1.07);
        }

        .det-hero-v6:nth-child(5) {
          transform: rotateY(-22deg) scale(1.02) translateX(2.5vw) translate3d(53px, 9px, -160px);
        }

        // Safari styles
        .det-hero-v6_safari:nth-child(2) {
          transform: rotateY(15deg) translateX(-3vw) scale(1.2);
        }

        .det-hero-v6_safari:nth-child(4) {
          transform: rotateY(-15deg) translateX(3vw) scale(1.2);
        }
          
      }

      @media screen and (max-width: 1100px) {
        display: flex;
        justify-content: center;
        width: 100%;
        gap: 0;
        perspective: 1000px;

        .det-hero-v6:nth-child(1),
        .det-hero-v6:nth-child(5) {
          display: none;
        }

        .det-hero-v6:nth-child(2) {
          transform: rotateY(20deg) translateX(-16vw) scale(1.27);
        }

        .det-hero-v6:nth-child(4) {
          transform: rotateY(-20deg) translateX(16vw) scale(1.27);
        }
      }

      @media screen and (max-width: 768px) {
        .det-hero-v6:nth-child(2) {
          transform: rotateY(20deg) translateX(-22vw) scale(1.65);
        }

        .det-hero-v6:nth-child(4) {
          transform: rotateY(-20deg) translateX(22vw) scale(1.65);
        }
      }

      @media screen and (max-width: 600px) {
        .det-hero-v6 {
          width: 220px;
        }

        .det-hero-v6:nth-child(2) {
          transform: rotateY(20deg) translateX(-8vw) scale(1.65);
        }

        .det-hero-v6:nth-child(4) {
          transform: rotateY(-20deg) translateX(8vw) scale(1.65);
        }
      }

      @media screen and (max-width: 400px) {
        .det-hero-v6 {
          width: 220px;
        }

        .det-hero-v6:nth-child(2) {
          transform: rotateY(20deg) translateX(-29vw) scale(1.65);
        }

        .det-hero-v6:nth-child(4) {
          transform: rotateY(-20deg) translateX(29vw) scale(1.65);
        }
      }
    `}
`;

const imageStyles = css`
  position: relative;
  overflow: hidden;
  height: 685px;
  width: 513px;

  @media screen and (max-width: 1560px) {
    height: 655px;
    width: 484px;
    z-index: 4;
  }

  @media screen and (max-width: 1480px) {
    width: 400px;
    height: 540px;
    z-index: 4;
  }

  @media screen and (max-width: 991px) {
    width: 400px !important;
    height: 540px !important;
    z-index: 4;
  }

  @media screen and (max-width: 600px) {
    width: 400px !important;
    height: 540px !important;
    z-index: 4;
  }
`;

const imageStylesOnlyMockup = css`
  position: relative;
  height: 685px;
  width: 513px;

  @media screen and (max-width: 1560px) {
    height: 655px;
    width: 484px;
    z-index: 4;
  }

  @media screen and (max-width: 1480px) {
    width: 400px;
    height: 540px;
    z-index: 4;
  }

  @media screen and (max-width: 991px) {
    width: 80vw !important;
    height: 540px !important;
    z-index: 4;
  }

  @media screen and (max-width: 600px) {
    width: 130vw !important;
    height: 640px !important;
    z-index: 4;
  }
`;

const BookMediaImg = styled(ImageComp)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
`;

export const V7: FC<HeroDetailedProps> = (props) => {
  const {
    mockup,
    cover_thumbnail,
    title = defaultWebValue,
    content = defaultWebValue,
    button = defaultWebValue,
    backgroundColor = COLOR_YELLOW,
    textColor,
    benefits = [],
    meta,
    hidden,
    pageImages,
    customBackgroundColor,
    order
  } = props;

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();
  const { isCoverDisabled, pages, bookMedia } = useBook();
  const { pagesThumbnails, updateImageMeta, purchaseElementId, type, renderSales, isDashboard } =
    useSales();
  const { handleUploadAction } = useGeneralActions(props);
  const [resize, setResize] = useState({
    width: meta?.['width'] ?? 0,
    height: meta?.['height'] ?? 0
  });

  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { isEmpty: isContentEmpty } = useIsEmpty(meta, 'content');

  const [isLoading, setIsLoading] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [sublineColor, setSublineColor] = useState<string>('#fff');
  const topRef = useRef<HTMLDivElement>(null);

  useSublineColor(topRef, setSublineColor, renderSales);

  const { checkBenefits: checkTitleBenefits } = useIsEmpty(meta, 'title', true);
  const { checkBenefits: checkContentBenefits } = useIsEmpty(meta, 'content', true);

  //check if browser is safari
  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    const isSafariBrowser =
      ua.includes('safari') && !ua.includes('chrome') && !ua.includes('android');
    setIsSafari(isSafariBrowser);
  }, []);

  const coverThumbnail = useMemo(() => {
    if (isCoverDisabled && (pages.length > 0 || pagesThumbnails.length > 0)) {
      return pagesThumbnails[0] || pages[0]?.image_preview;
    }
    return cover_thumbnail;
  }, [isCoverDisabled, pages, pagesThumbnails, cover_thumbnail]);

  const bookMockup = useMemo(() => {
    if (meta['image'] && !meta['image_placeholder']) {
      return meta['image'];
    }

    const media = bookMedia?.find((media) => media.name === 'book_mockup');
    return media?.url;
  }, [bookMedia, meta]);

  const galleryimages = useMemo(() => {
    if ((pageImages ?? []).length === 0 || (pageImages ?? []).length === 1 || !pageImages) {
      return [];
    }

    const listWithoutFirst = pageImages.slice(1);

    if (listWithoutFirst.length === 1) {
      return [];
    }

    if (listWithoutFirst.length <= 3 || listWithoutFirst.length === 2)
      return listWithoutFirst.slice(0, 2).map((page) => page);

    if (listWithoutFirst.length > 3) return listWithoutFirst.slice(0, 4).map((page) => page);

    return [];
  }, [pageImages]);

  const resolvedBackgroundColor = useMemo(() => {
    if (meta?.['background_color']?.includes('background_color4') && customBackgroundColor) {
      return COLOR_LIGHTER_GRAY;
    }
    return customBackgroundColor ? customBackgroundColor : shade(backgroundColor, 0.7);

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backgroundColor, customBackgroundColor]);

  const opacity = meta?.['background_color']?.includes('faded_color_')
    ? 0.7
    : meta?.['background_color']?.startsWith('color') ||
        meta?.['background_color']?.startsWith('base_color1')
      ? 0.2
      : 0.7;

  // Accessibility improvements
  const titleAccessKey = useMemo(() => generateAccessKey('ht', order), [order]);
  const contentAccessKey = useMemo(() => generateAccessKey('hc', order), [order]);
  const buttonAccessKey = useMemo(() => generateAccessKey('hb', order), [order]);
  const galleryAccessKey = useMemo(() => generateAccessKey('hgal', order), [order]);
  const skipLinkId = useMemo(() => `skip-to-hero-${meta['accessorKey'] || 'hero'}`, [meta]);

  const buttonType = useMemo(() => {
    return meta['url']
      ? 'external link'
      : type === 'delivery'
        ? 'delivery action'
        : 'purchase button';
  }, [meta, type]);

  const handleEditError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Hero edit error: ${error instanceof Error ? error.message : 'Edit action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleModalError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Button action error: ${error instanceof Error ? error.message : 'Action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleUploadError = (error: Error | unknown) => {
    // Announce upload error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Image upload error: ${error instanceof Error ? error.message : 'Upload failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleUpload = async (file: File) => {
    try {
      setIsLoading(true);
      await handleUploadAction(file, setResize, 'book_mockup');
      setIsLoading(false);
    } catch (error) {
      handleUploadError(error);
      setIsLoading(false);
    }
  };

  return (
    <HeroDetailed
      ref={topRef}
      className={containerClassname}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1,
        backgroundColor: sublineColor
      }}
      aria-label="Hero section with centered layout, decorative stars, and rounded background"
      role="region">
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to hero detailed content</a>
      </span>

      <StyledWrapper id={skipLinkId}>
        <TitleWrapper id="HeadlineSection">
          <Title
            id={title.id}
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(title.val, isDashboard)
            }}
            style={{
              color: filterBackgroundColor(resolvedBackgroundColor, textColor)
            }}
            isEmpty={isTitleEmpty}
            {...(isDashboard
              ? {
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    try {
                      updateActiveElement(e);
                    } catch (error) {
                      handleEditError(error);
                    }
                  },
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta['accessorKey'],
                  'data-accessorkey': 'title',
                  'data-placeholder': '+',
                  editable: true
                }
              : {
                  'aria-label':
                    isTitleEmpty || !title.val?.trim() ? 'Hero section main title' : undefined,
                  accessKey: titleAccessKey,
                  title: `Press Alt+${titleAccessKey} to focus this hero title`,
                  tabIndex: 0
                })}
          />
          <Description
            id={content.id}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(content.val, editable) }}
            style={{
              color: filterBackgroundColor(resolvedBackgroundColor, textColor)
            }}
            isEmpty={isContentEmpty}
            {...(isDashboard
              ? {
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    try {
                      updateActiveElement(e);
                    } catch (error) {
                      handleEditError(error);
                    }
                  },
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta['accessorKey'],
                  'data-accessorkey': 'content',
                  'data-placeholder': '+',
                  editable: true
                }
              : {
                  role: 'textbox',
                  'aria-multiline': 'true',
                  'aria-label':
                    isContentEmpty || !content.val?.trim() ? 'Hero section description' : undefined,
                  'aria-describedby': title.id,
                  accessKey: contentAccessKey,
                  title: `Press Alt+${contentAccessKey} to focus this hero description`,
                  tabIndex: 0
                })}
          />
        </TitleWrapper>
        <ButtonInner>
          <BtnInner role="group" aria-label={`Hero action area - ${buttonType}`}>
            {meta['url'] || type === 'delivery' ? (
              <>
                <DownloadStyled
                  textColor={textColor}
                  button={button}
                  meta={meta}
                  outerOnClick={() => {
                    if (editable) {
                      try {
                        // empty needs only for error
                      } catch (error) {
                        handleModalError(error);
                      }
                    }
                  }}
                  {...(!isDashboard && {
                    accessKey: buttonAccessKey,
                    title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                    'aria-describedby': `${title.id} ${content.id}`
                  })}
                />
                <ArrowsSvgStyled
                  iconName="icon_1"
                  backgroundColor={filterBackgroundColor(resolvedBackgroundColor, textColor)}
                />
              </>
            ) : (
              <>
                <StyledButton
                  to={purchaseElementId}
                  button={button}
                  meta={meta}
                  outerOnClick={() => {
                    if (editable) {
                      try {
                        // empty needs only for error
                      } catch (error) {
                        handleModalError(error);
                      }
                    }
                  }}
                  {...(!isDashboard && {
                    accessKey: buttonAccessKey,
                    title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                    'aria-describedby': `${title.id} ${content.id}`
                  })}
                />
                <ArrowsSvgStyled
                  iconName={'icon_1'}
                  backgroundColor={filterBackgroundColor(resolvedBackgroundColor, textColor)}
                />
              </>
            )}
          </BtnInner>
        </ButtonInner>
        <SvgStar
          style={{
            opacity: opacity - 0.1
          }}
          xmlns="http://www.w3.org/2000/svg"
          width="93"
          height="93"
          viewBox="0 0 93 93"
          fill="none"
          aria-hidden="true"
          role="presentation"
          focusable="false">
          <path
            d="M46.5 0L56.6601 36.3399L93 46.5L56.6601 56.6601L46.5 93L36.3399 56.6601L0 46.5L36.3399 36.3399L46.5 0Z"
            fill="white"
          />
        </SvgStar>
        <SvgStar2
          style={{
            opacity: opacity - 0.1
          }}
          className="star"
          xmlns="http://www.w3.org/2000/svg"
          width="93"
          height="93"
          viewBox="0 0 93 93"
          fill="none"
          aria-hidden="true"
          role="presentation"
          focusable="false">
          <path
            d="M46.5 0L56.6601 36.3399L93 46.5L56.6601 56.6601L46.5 93L36.3399 56.6601L0 46.5L36.3399 36.3399L46.5 0Z"
            fill="white"
          />
        </SvgStar2>
      </StyledWrapper>

      <ImageGallery
        count={galleryimages.length}
        role="region"
        aria-label={`Book gallery with ${galleryimages.length > 0 ? galleryimages.length + 1 : 1} preview images`}
        {...(!isDashboard && {
          accessKey: galleryAccessKey,
          title: `Press Alt+${galleryAccessKey} to focus the image gallery`,
          tabIndex: 0
        })}>
        {galleryimages.slice(0, galleryimages.length / 2).map((image, index) => (
          <div
            className={`det-hero-v6${isSafari ? ` det-hero-v6_safari` : ''}`}
            key={index}
            role="img"
            aria-label={`Gallery preview image ${index + 1} of ${galleryimages.length + 1}`}
            tabIndex={isDashboard ? -1 : 0}>
            <img
              src={image}
              alt={`Book preview page ${index + 1} showing content layout and design`}
            />
          </div>
        ))}

        <div
          className={`det-hero-v6${isSafari ? ` det-hero-v6_safari` : ''}`}
          role="img"
          aria-label={`Main book cover ${galleryimages.length > 0 ? `- center of ${galleryimages.length + 1} gallery images` : '- single preview'}`}>
          {bookMockup ? (
            <ConditionalImage
              isSalePage
              css={
                galleryimages.length > 1 ? (imageStyles as never) : (imageStylesOnlyMockup as never)
              }
              disabled={!editable}
              resizeSelector={editable}
              enableLinks
              updateMeta={(value) => updateImageMeta(meta['accessorKey'], value, 'image_link')}
              handleResize={({ width, height }) => {
                setResize({ width, height });
                updateImageMeta(meta['accessorKey'], width, 'width');
                updateImageMeta(meta['accessorKey'], height, 'height');
              }}
              onUpload={handleUpload}
              loading={isLoading}
              alreadyUploaded={Boolean(bookMockup)}
              imageFile={bookMockup}
              initialPosition={{
                x: meta['x_1'] || 0,
                y: meta['y_1'] || 0,
                width: meta['d_width_1'],
                height: meta['d_height_1']
              }}
              meta={meta}
              positionEnabled
              handlePosition={(width: number, height: number, x: number, y: number) => {
                updateImageMeta(meta['accessorKey'], x, 'x_1');
                updateImageMeta(meta['accessorKey'], y, 'y_1');
                updateImageMeta(meta['accessorKey'], width, 'd_width_1');
                updateImageMeta(meta['accessorKey'], height, 'd_height_1');
              }}
              imageAspectRatio={{
                ratio: '3x4',
                pixels: '600x800'
              }}
              ariaLabel="Main book cover image - drag and drop to upload new image">
              <BookMediaImg
                src={bookMockup}
                alt="Main book cover preview with centered layout and star decoration"
                width={resize.width || 500}
                height={resize.height || 500}
                priority
              />
            </ConditionalImage>
          ) : (
            <StyledMockup
              mockup={mockup?.file}
              mockup_id={mockup?.id}
              cover_thumbnail={coverThumbnail}
              aria-label="Book mockup with cover thumbnail in centered star layout"
            />
          )}
        </div>

        {galleryimages.slice(galleryimages.length / 2, galleryimages.length).map((image, index) => (
          <div
            className={`det-hero-v6${isSafari ? ` det-hero-v6_safari` : ''}`}
            key={index}
            role="img"
            aria-label={`Gallery preview image ${Math.ceil(galleryimages.length / 2) + index + 2} of ${galleryimages.length + 1}`}
            tabIndex={isDashboard ? -1 : 0}>
            <img
              src={image}
              alt={`Book preview page ${Math.ceil(galleryimages.length / 2) + index + 2} showing content layout and design`}
            />
          </div>
        ))}
      </ImageGallery>

      <BenefitWrapper>
        <Wrapper>
          <Benefits role="region" aria-label="Key benefits and features without background">
            {benefits.map(({ content, icon, title }, index) => {
              const isTitleBenEmpty = checkTitleBenefits(index);
              const isContentBenEmpty = checkContentBenefits(index);
              const benefitAccessKey = generateAccessKey('hben', index);

              return (
                <BenefitItem
                  key={`${icon?.toString()}-${index}`}
                  role="article"
                  aria-label={`Benefit ${index + 1}: ${title?.val || 'Key feature'}`}
                  {...(!isDashboard && {
                    accessKey: benefitAccessKey,
                    title: `Press Alt+${benefitAccessKey} to focus benefit ${index + 1}`,
                    tabIndex: 0
                  })}>
                  <StyledSvgIcons
                    iconName={`icon_${index + 1}` as 'icon_1' | 'icon_2' | 'icon_3'}
                    backgroundColor={button.color}
                    aria-hidden="true"
                  />
                  <BenefitTxtInner>
                    <TitleBenefit
                      id={title?.id}
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(title?.val, editable) }}
                      style={{ color: filterBackgroundColor(sublineColor, textColor) }}
                      isEmpty={isTitleBenEmpty}
                      {...(isDashboard
                        ? {
                            onClick: (e: React.MouseEvent<HTMLElement>) => {
                              try {
                                updateActiveElement(e);
                              } catch (error) {
                                handleEditError(error);
                              }
                            },
                            onBlur: resetActiveElement,
                            'data-accessor-parent': meta['accessorKey'],
                            'data-accessorkey': 'title',
                            'data-benefits': true,
                            'data-placeholder': '+',
                            editable: true
                          }
                        : {
                            'aria-label': !title?.val?.trim()
                              ? `Benefit ${index + 1} title`
                              : undefined
                          })}
                    />
                    <TextBenefit
                      id={content?.id}
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(content?.val, editable) }}
                      style={{ color: filterBackgroundColor(sublineColor, textColor) }}
                      isEmpty={isContentBenEmpty}
                      {...(isDashboard
                        ? {
                            onClick: (e: React.MouseEvent<HTMLElement>) => {
                              try {
                                updateActiveElement(e);
                              } catch (error) {
                                handleEditError(error);
                              }
                            },
                            onBlur: resetActiveElement,
                            'data-accessor-parent': meta['accessorKey'],
                            'data-accessorkey': 'content',
                            'data-benefits': true,
                            'data-placeholder': '+',
                            editable: true
                          }
                        : {
                            'aria-label': !content?.val?.trim()
                              ? `Benefit ${index + 1} description`
                              : undefined,
                            'aria-describedby': title?.id
                          })}
                    />
                  </BenefitTxtInner>
                </BenefitItem>
              );
            })}
          </Benefits>
        </Wrapper>
      </BenefitWrapper>

      <WhiteInner
        style={{
          opacity: opacity
        }}
        aria-hidden="true"
        role="presentation">
        <RelativeWhite>
          <div className="hero-white-v5 hero-white-v5_one"></div>
          <div className="hero-white-v5 hero-white-v5_two"></div>
          <div className="hero-white-v5 hero-white-v5_three"></div>
        </RelativeWhite>
      </WhiteInner>

      <ColoredBg
        style={{ backgroundColor: resolvedBackgroundColor }}
        aria-hidden="true"
        role="presentation"
      />
    </HeroDetailed>
  );
};

V7.displayName = 'HeroDetailedV7';
